/**
 * @file index.tsx
 *
 * @fileoverview Creates a split view layout (list on the left detail on the right).
 */
import { cva } from "class-variance-authority";
import { ReactNode } from "react";
import Box from "../../../02-atoms/07-box";
import styles from "./splitView.module.scss";

const sideViewSidebar = cva([styles.sideViewSidebar], {
  variants: {
    inDetail: {
      true: styles.sideViewSidebarInDetail,
    },
  },
});

const sideViewMain = cva([styles.sideViewMain], {
  variants: {
    inDetail: {
      false: styles.sideViewMainNotInDetail,
    },
  },
});

export interface SplitViewLayoutProps {
  /**
   * The content to show on the left side.
   */
  sidebar: ReactNode;
  /**
   * The title HTML tag.
   *
   * @default null
   */
  Title?: ReactNode;
  /**
   * The main content to show (detail).
   */
  children: ReactNode;
  /**
   * If a detail is being shown (for mobile).
   *
   * @default "false"
   */
  inDetail?: boolean;
  /**
   * The className.
   * This prop gets appended to the already existing classes.
   */
  className?: string;
}

/**
 * Displays a split view layout where a list of items is
 * shown on the left and the detail is shown on the right.
 */
export const SplitViewLayout = ({
  sidebar,
  children,
  inDetail = false,
  Title = null,
  className = "",
}: SplitViewLayoutProps) => {
  return (
    <Box
      className={`${styles.sideViewWrapper} ${className} jcl-search-split-view`}
    >
      <div className={sideViewSidebar({ inDetail })}>
        {Title && <div className={styles.sideTitle}>{Title}</div>}
        {sidebar}
      </div>
      <div className={sideViewMain({ inDetail })}>{children}</div>
    </Box>
  );
};
